import MiniCartProductDescription from "@auer-steel/CustomComponents/Cart/MiniCartProductDescription";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    getCurrentCartState,
    isCartCheckoutDisabled,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import CartTotalDisplay from "@insite/content-library/Components/CartTotalDisplay";
import ProductImage from "@insite/content-library/Components/ProductImage";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, CSSProperties } from "styled-components";

export interface MiniCartStyles {
    container: InjectableCss;
    productInfo: InjectableCss;
    cartInfo: InjectableCss;
    actions: InjectableCss;
    topBar: InjectableCss;
    productButton: InjectableCss;
    minicartInfoGridItem?: GridItemProps;
}
export const miniCartStyles: MiniCartStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    productInfo: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
            text-align: right;
        `,
    },
    cartInfo: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: 10px;
            margin-bottom: 10px;
        `,
    },
    productButton: {
        css: css`
            text-align: right;
        `,
    },
    actions: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
        `,
    },
    topBar: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            margin-bottom: 10px;
        `,
    },
    minicartInfoGridItem: {
        width: 3,
    },
};

const typographyProps = {
    css: css`
        text-align: right;
        margin-bottom: 0em;
    `,
};

interface OwnProps {
    addedProduct: any;
    qty: any;
    price: any;
    inventory: any;
}

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled: !cart || isCartCheckoutDisabled(cart),
        isCartEmpty: isCartEmpty(cart),
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        isPricingHiddenByRole: Boolean(getSettingsCollection(state).productSettings.isPricingHiddenByRole),
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const MiniCart: FC<Props> = ({
    cart,
    addedProduct,
    qty,
    price,
    inventory,
    cartUrl,
    checkoutShippingPageUrl,
    isPricingHiddenByRole,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    isCartEmpty,
    preloadCheckoutShippingData,
}) => {
    const checkoutHandler = () => {
        preloadCheckoutShippingData({
            onSuccess: () => {
                // history.push(checkoutShippingPageUrl!);
                window.location.href = checkoutShippingPageUrl!; // workaround for possible base bug in SpireRouter.tsx regarding history context
            },
        });
    };
    const cartHandler = () => {
        // history.push(checkoutShippingPageUrl!);
        window.location.href = cartUrl!; // workaround for possible base bug in SpireRouter.tsx regarding history context
    };
    return (
        <div className="flex-container" style={{ marginTop: "1em" }}>
            <div className="flex-left" style={{ width: "50%", display: "inline-block" }}>
                <Typography variant="h3" weight="bold" data-test-selector="cdtMiniCart">
                    {translate("My Cart")}
                </Typography>
            </div>
            <div className="flex-right" style={{ width: "50%", display: "inline-block" }}>
                <Typography variant="p" weight="bold" data-test-selector="cdtMiniCart" {...typographyProps}>
                    {cart?.totalCountDisplay} {translate("Item(s)")}
                </Typography>
            </div>
            <div className="flex-right" style={{ width: "100%", display: "inline-block" }}>
                <hr />
            </div>
            <div className="flex-container" style={{ display: "flex", alignItems: "center" }}>
                <div className="flex-right" style={{ width: "15%", margin: "1em", display: "inline-block" }}>
                    <ProductImage product={addedProduct}></ProductImage>
                </div>
                <div className="flex-right" style={{ width: "30%", margin: "1em", display: "inline-block" }}>
                    <MiniCartProductDescription
                        product={addedProduct}
                        productDto={addedProduct}
                    ></MiniCartProductDescription>
                </div>
                <div className="flex-left" style={{ width: "40%", margin: "0em", display: "inline-block" }}>
                    {!isPricingHiddenByRole && (
                        <Typography variant="p" weight="bold" data-test-selector="cdtMiniCartPrice" {...typographyProps}>
                            {price}
                        </Typography>
                    )}
                    <Typography variant="p" weight="bold" data-test-selector="cdtMiniCartQuantity" {...typographyProps}>
                        Quantity: {qty}
                    </Typography>
                    <Typography
                        variant="p"
                        weight="bold"
                        data-test-selector="cdtMiniCartInventory"
                        {...typographyProps}
                    >
                        {inventory > 0 ? siteMessage("Inventory_AvailableStock") : siteMessage("Inventory_OutofStock")}
                    </Typography>
                </div>
            </div>
            <div>
                <StyledWrapper {...miniCartStyles.actions}>
                    <Button onClick={cartHandler}>{translate("View Cart")}</Button>
                    {canCheckoutWithCart && (
                        <Button onClick={checkoutHandler} disabled={isCartCheckoutDisabled}>
                            {translate("Checkout")}
                        </Button>
                    )}
                </StyledWrapper>
            </div>
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniCart);
